import { Avatar, SxProps, Theme, alpha, darken, styled } from '@mui/material'
import { MouseEventHandler, forwardRef, useMemo } from 'react'

import stringToHexColor from '../../utils/stringToHexColor'

type AvatarSize = 'tiny' | 'small' | 'medium' | 'large'

export interface NameAvatarProps {
  color?: string
  firstName: string
  lastName: string
  opacity?: number
  size?: AvatarSize
  sx?: SxProps<Theme>
  onClick?: MouseEventHandler<HTMLDivElement>
}

const NameAvatar = forwardRef<HTMLDivElement, NameAvatarProps>(
  ({ color, firstName, lastName, opacity = 0.6, size = 'medium', sx, onClick, ...props }, ref) => {
    const _color = useMemo(
      () => color || stringToHexColor(firstName + lastName),
      [color, firstName, lastName]
    )

    const initials = useMemo(
      () =>
        (firstName ? firstName[0].toUpperCase() : '') +
        (lastName?.length >= 1 ? lastName[0].toUpperCase() : ''),
      [firstName, lastName]
    )

    return (
      <StyledAvatar
        ref={ref}
        {...props}
        sx={{
          bgcolor: alpha(_color, opacity),
          color: darken(_color, 0.5),
          ...sizes[size],
          ...sx,
        }}
        onClick={onClick ?? undefined}
      >
        <span>{initials}</span>
      </StyledAvatar>
    )
  }
)

export const sizes: Record<
  AvatarSize,
  { fontSize: number; height: number; lineHeight: string; width: number }
> = {
  tiny: { fontSize: 10, height: 18, lineHeight: '18px', width: 18 },
  small: { fontSize: 11, height: 24, lineHeight: '24px', width: 24 },
  medium: { fontSize: 16, height: 36, lineHeight: '36px', width: 36 },
  large: { fontSize: 30, height: 56, lineHeight: '56px', width: 56 },
}

const StyledAvatar = styled(Avatar)`
  @font-face {
    font-family: 'fallbackAvatarFont';
    src: local('Arial');
    font-weight: 600 !important;
  }

  font-family: 'Arial Rounded MT Bold', ArialRoundedMTBold, fallbackAvatarFont;
`

export default NameAvatar
